/**
 * Decimal conversion utilities for trading precision
 * Critical: All values must be scaled correctly for smart contract calls
 */

/**
 * Parse a USDC amount string to bigint with 6 decimals
 * @param amount - Amount in USDC (e.g., "100.50" = 100.50 USDC)
 * @returns Bigint representation with 6 decimal precision
 */
export function parseUsdc(amount: string | number): bigint {
  const amountStr = typeof amount === 'number' ? amount.toString() : amount;
  if (!amountStr || amountStr === '0') return 0n;

  if (!/^\d+(\.\d+)?$/.test(amountStr)) {
    throw new Error(`Invalid USDC amount: ${amountStr}`);
  }

  const [intPart, decPart = ''] = amountStr.split('.');
  const paddedDec = decPart.padEnd(6, '0').slice(0, 6);
  return BigInt(intPart + paddedDec);
}

/**
 * Parse a price string to bigint with 18 decimals.
 * Uses string manipulation to avoid floating-point precision loss.
 * @param price - Price as string (e.g., "1900.50")
 * @returns Bigint representation with 18 decimal precision
 */
export function parsePrice(price: string | number): bigint {
  const priceStr = typeof price === 'number' ? price.toString() : price;
  if (!priceStr || priceStr === '0') return 0n;

  if (!/^-?\d+(\.\d+)?$/.test(priceStr)) {
    throw new Error(`Invalid price: ${priceStr}`);
  }

  const isNegative = priceStr.startsWith('-');
  const absStr = isNegative ? priceStr.slice(1) : priceStr;
  const [intPart, decPart = ''] = absStr.split('.');
  const paddedDec = decPart.padEnd(18, '0').slice(0, 18);
  const result = BigInt(intPart + paddedDec);

  return isNegative ? -result : result;
}

/**
 * Parse leverage to contract format (leverage × 100).
 * @param leverage - Leverage as number (e.g., 20 for 20x)
 * @returns Bigint (e.g., 2000 for 20x)
 */
export function parseLeverage(leverage: number): bigint {
  if (leverage <= 0 || !Number.isFinite(leverage)) {
    throw new Error(`Invalid leverage: ${leverage}`);
  }
  return BigInt(Math.round(leverage * 100));
}
